import { DISCOUNT_CLASS_TYPES, EXTERNAL_LOYALTY_PARTNERS } from "../../../../data/constants";

/**
 * Releasing a DotPe CRM discount when the bill it was issued against changes.
 *
 * ## What it is for
 *
 * A DotPe CRM offer is BLOCKED at the provider when it is applied — the same contract loyalty
 * points have (`docs/flows/LOYALTY_REFUND.md`). Edit the bill afterwards and the block is
 * against a total that no longer exists, so the coupon has to be released and taken off the
 * sale. `sibling loyaltyRefundEffect` does exactly this for points; this does it for the coupon.
 *
 * ⚠️ **It became reachable on 2026-09-09 (DM-146)**, when DotPe joined
 * `EXTERNAL_LOYALTY_PROVIDER_METAIDS`. Before that `removeDiscountFromCart` would strip the
 * discount locally and send no unblock, so an auto-release here would have quietly leaked the
 * customer's coupon.
 *
 * ## Why a folder rather than a branch inside `loyaltyRefundEffect`
 *
 * The removal itself is `helpers/saleHelpers`' `removeDiscountFromCart`, which exists on WEB
 * ONLY — mobile's app has no discount-removal path to uplift, so there is nothing for the native
 * variant to call. That is a genuine platform gap, and rule 4's three files are how it gets
 * stated once instead of being smuggled into a shared file that then fails to bundle.
 *
 * This file holds the part that has no platform half: which discounts qualify.
 */

/**
 * A DotPe-issued CRM discount, by the same three-field shape the apply path writes.
 *
 * ⚠️ All three matter. `dclass` separates a CRM *discount* from an ordinary *deal*, `source`
 * separates one the partner issued from one the merchant configured, and `couponProvider` names
 * the partner — a bill can carry a coupon from a partner that is no longer the active one, so
 * the provider is read off the DISCOUNT, never off the active provider.
 */
export function isDotpeExternalDiscount(discount: any): boolean {
  return (
    discount?.dclass === DISCOUNT_CLASS_TYPES.discount &&
    discount?.couponProvider === EXTERNAL_LOYALTY_PARTNERS.DOTPE &&
    discount?.source === "external"
  );
}
